// UI strings (English only in v1). Kept in one place so i18next can take over later (specs/09 FR-44).
import type { AccountErrorKind } from "../core/account/types";
import type { CameraErrorKind } from "../core/platform";
import type { PriceField } from "../core/pricing";
import type { CardLanguage } from "../core/types";

const fieldNames: Record<PriceField, string> = {
  avg30: "30-day average",
  trend: "trend",
  avg7: "7-day average",
  low: "lowest price",
};

export const en = {
  appName: "PokeJournal",
  nav: {
    home: "Home",
    scan: "Scan",
    portfolio: "Portfolio",
    search: "Search",
    account: "Account and settings",
  },
  dashboard: {
    title: "Home",
    scan: "Scan a card",
    search: "Search",
    importPortfolio: "Import portfolio",
    topCards: "Most valuable cards",
    viewAll: "View portfolio",
    noTopCards: "No priced cards yet. Refresh prices to see your most valuable cards.",
    byLanguage: "By language",
    bySet: "By set",
    otherSets: (n: number) => `${n} other ${n === 1 ? "set" : "sets"}`,
    cards: (n: number) => `${n} ${n === 1 ? "card" : "cards"}`,
    empty: {
      title: "Welcome to PokeJournal",
      body: "Scan or search for your Pokémon cards in English, Japanese or Chinese, and see what your collection is worth in euros. Already have a PokeJournal export? Import it.",
    },
  },
  notFound: { title: "Page not found", home: "Go to the scanner" },
  loading: "Loading…",
  disclaimer:
    "PokeJournal is an unofficial fan project, not affiliated with Nintendo, The Pokémon Company, Game Freak or Creatures.",
  account: {
    tagline: "Track the value of your Pokémon card collection. Scan a card, see its market price.",
    inviteOnly: "PokeJournal is invite-only.",
    privacy: "Privacy",
    signIn: {
      title: "Sign in",
      google: "Continue with Google",
      or: "or",
      email: "Email",
      password: "Password",
      submit: "Sign in",
      working: "Signing in…",
      forgot: "Forgot password?",
    },
    welcome: {
      title: "Welcome to PokeJournal",
      body: (email: string) =>
        `You're signed in as ${email}. How do you want to sign in next time?`,
      setPassword: "Set a password",
      newPassword: "Password",
      passwordHint: "At least 10 characters.",
      save: "Save password",
      useGoogle: "I'll use Google",
      googleHint: "Next time, use “Continue with Google” with the Google account for this email.",
      invalid:
        "This invite link has expired or was already used. Sign in, or ask for a new invite.",
    },
    reset: {
      title: "Reset password",
      requestBody: "Enter your email and we'll send you a link to set a new password.",
      email: "Email",
      send: "Send reset link",
      sending: "Sending…",
      sent: "If that email has an account, a reset link is on its way. Check your inbox.",
      googleNote:
        "Signed in with Google before? Then you have no password: use “Continue with Google”.",
      newTitle: "Set a new password",
      newPassword: "New password",
      save: "Save password",
      saved: "Your password is saved.",
      expired: "This link has expired. Send a new one below.",
      backToSignIn: "Back to sign in",
    },
    passwordErrors: {
      short: "Use at least 10 characters.",
      long: "Use at most 72 characters.",
    },
    emailInvalid: "Enter a valid email address.",
    errors: {
      "invalid-credentials": "Email or password is incorrect.",
      "rate-limit": "Too many attempts. Try again in a few minutes.",
      "weak-password": "That password is too weak. Choose a longer or less common one.",
      "same-password": "That's your current password. Choose a different one.",
      "email-not-confirmed": "Open the invite email first, then try again.",
      network: "Can't reach the sign-in service. Check your connection.",
      "link-expired": "This link has expired. Ask for a new invite, or send a new reset link.",
      "google-not-invited": "This Google account hasn't been invited to PokeJournal.",
      "google-cancelled": "Google sign-in was cancelled.",
      "google-failed": "Google sign-in didn't work. Try again or use email.",
      unknown: "Something went wrong. Try again.",
    } as Record<AccountErrorKind, string>,
    unreachable: {
      title: "Can't reach the sign-in service",
      body: "Check your connection. If it's working, the service may be down for a moment.",
      retry: "Retry",
    },
    loadFailed: {
      title: "Couldn't load your portfolio",
      body: "Check your connection and try again.",
      retry: "Retry",
    },
    notConfigured: {
      title: "PokeJournal isn't set up",
      body: "This build has no sign-in service configured (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_PUBLISHABLE_KEY).",
    },
    saveFailed: "Couldn't save. Check your connection.",
    settingsSaveFailed: "Couldn't save your settings.",
  },
  settings: {
    title: "Settings",
    values: {
      title: "Values",
      currency: "Currency",
      currencies: { EUR: "Euro (EUR)", USD: "US dollar (USD)" },
      currencyHint:
        "Prices come from Cardmarket in euros. In US dollars, amounts are converted at the European Central Bank's daily rate.",
      rateLoading: "Getting today's exchange rate…",
      rateReady: (rate: string, date: string) => `1 EUR = ${rate} USD (ECB, ${date})`,
      rateFailed: "Couldn't get the exchange rate, so amounts are shown in EUR for now.",
      retry: "Try again",
      priceSource: "Price source",
      priceSourceValue: "Cardmarket",
      priceSourceHint:
        "TCGplayer prices are shown on English card pages for information, but aren't used in totals.",
      priceField: "Price used for values",
      priceFieldHint: "If a card doesn't have this figure, its trend price is used.",
      priceFields: {
        avg30: "30-day average (recommended)",
        trend: "Trend",
        avg7: "7-day average",
        low: "Lowest price",
      } as Record<PriceField, string>,
      conditions: "Condition multipliers",
      conditionsHint:
        "Market prices are for Near Mint cards. Other conditions count for this share of the price.",
      percentInvalid: "Enter a whole number from 0 to 100.",
      reset: "Reset to defaults",
      resetDone: "Settings reset to their defaults.",
    },
    scanning: {
      title: "Scanning",
      body: "The card language the scanner and search start with.",
    },
    ocr: {
      title: "Text recognition models",
      body: "Scanning reads cards on your device with these models. They're downloaded the first time you scan in a language and kept in this browser.",
      models: {
        eng: "Latin script (numbers and set codes on every card, and English names)",
        jpn: "Japanese names",
        chi_sim: "Simplified Chinese names",
      },
      notDownloaded: "Not downloaded",
      remove: "Remove",
      removing: "Removing…",
      removeLabel: (model: string) => `Remove ${model}`,
      unavailable: "Couldn't check the downloaded models in this browser.",
    },
    data: {
      title: "Data",
      body: "Export your portfolio as a backup or to use in a spreadsheet, or import a file you exported earlier.",
      clear: "Clear portfolio",
      clearTitle: "Clear your portfolio?",
      clearBody: (n: number) =>
        `This removes all ${n} ${n === 1 ? "entry" : "entries"} from your portfolio, on every device. You can undo it for a few seconds afterwards.`,
      clearConfirm: "Clear portfolio",
      cancel: "Cancel",
    },
    about: {
      title: "About",
      version: (v: string) => `Version ${v}`,
      sources:
        "Card data from TCGdex. Prices from Cardmarket, through TCGdex and the Pokémon TCG API, and TCGplayer for information. Exchange rates from the European Central Bank, through Frankfurter.",
    },
    account: {
      title: "Account",
      signedInAs: "Signed in as",
      method: "Signs in with",
      methods: { email: "Email and password", google: "Google" },
      signOut: "Sign out",
      signOutEverywhere: "Sign out on all devices",
      delete: "Delete account",
      deleteTitle: "Delete your account?",
      deleteBody:
        "This permanently deletes your account, your portfolio and your settings. It can't be undone, and you'd need a new invite to come back. Export your portfolio first if you want a copy.",
      deleteType: "Type DELETE to confirm",
      deleteConfirm: "Delete account",
      deleting: "Deleting…",
      cancel: "Cancel",
      deleteFailed: "Couldn't delete your account. Check your connection and try again.",
      googleNote: "This doesn't remove PokeJournal from your Google account. To do that too, open",
      googleLink: "Google's third-party connections",
    },
  },
  data: {
    export: "Export",
    import: "Import",
    saved: (name: string) => `Saved ${name}`,
    exportDialog: {
      title: "Export portfolio",
      format: "Format",
      json: "JSON",
      jsonHint: "A full backup. Import it again any time.",
      csv: "CSV",
      csvHint: "For Excel, Numbers or Google Sheets. Can be imported too, without photos.",
      photos: (n: number) => `Include scan photos (${n})`,
      photosHint: "Off by default: photos make the file much larger.",
      large: (mb: number) =>
        `This file will be about ${mb} MB. It can take a while, and some phones can't open files this large.`,
      progress: (done: number, total: number) => `Adding photos: ${done} of ${total}`,
      exporting: "Exporting…",
      confirm: "Export",
      cancel: "Cancel",
      failed: "Couldn't export your portfolio. Try again.",
    },
    importDialog: {
      reading: "Reading the file…",
      title: (n: number) => `Import ${n} ${n === 1 ? "entry" : "entries"}?`,
      from: (name: string, existing: number) =>
        `From ${name}. Your portfolio already has ${existing} ${existing === 1 ? "entry" : "entries"}.`,
      merge: "Add to my portfolio",
      mergeHint:
        "Cards you already have in the same variant and condition get their quantities added together.",
      replace: "Replace my portfolio",
      replaceHint: (n: number) =>
        `Removes your current ${n} ${n === 1 ? "entry" : "entries"} first.`,
      cancel: "Cancel",
      done: (n: number) => `Imported ${n} ${n === 1 ? "entry" : "entries"}`,
      photosNote:
        "Scan photos from the file are kept for this visit only. They aren't saved to your account.",
      close: "Close",
      errorTitle: "Couldn't import this file",
      unchanged: "Your portfolio hasn't changed.",
      tryAnother: "Choose another file",
    },
    errors: {
      notPokeJournal: "This isn't a PokeJournal file.",
      newerVersion:
        "This file was made by a newer version of PokeJournal. Reload the page to get the latest version, then try again.",
      noEntries: "This file has no cards in it.",
      invalidEntry: (row: number, csv: boolean) =>
        csv
          ? `Row ${row} isn't valid, so nothing was imported. Check that row and try again.`
          : `Entry ${row} in this file isn't valid, so nothing was imported.`,
      missingColumns: (columns: string[]) =>
        `This CSV file is missing columns: ${columns.join(", ")}.`,
      tooMany: (max: number) =>
        `A portfolio holds at most ${max.toLocaleString("en")} entries, and importing this file would go over that.`,
    },
  },
  privacy: {
    title: "Privacy",
    updated: "Last updated October 2026",
    sections: [
      {
        heading: "What's stored",
        body: "Your email address; your name and profile picture if you sign in with Google; your portfolio entries (cards, quantities, conditions, prices you paid, notes); and your settings. Scan photos are never stored: they stay on your device for the visit.",
      },
      {
        heading: "Where",
        body: "With Supabase, in its EU region (Frankfurt, Germany). Your browser keeps your sign-in session so a refresh doesn't sign you out.",
      },
      {
        heading: "Why",
        body: "To let you sign in and to keep your portfolio and settings the same on all your devices. Nothing is sold, shared or used for advertising.",
      },
      {
        heading: "Who can see it",
        body: "Only you can see your portfolio and settings. The owner of PokeJournal can see the email addresses of accounts, to manage invites. When a scan can't be read on your device, the photo is sent to Anthropic's Claude to read it, and isn't kept.",
      },
      {
        heading: "Deleting it",
        body: "Settings → Account → Delete account removes your account, portfolio and settings straight away. If you used Google, you can also remove PokeJournal in your Google account's third-party connections.",
      },
    ],
  },
  scan: {
    title: "Scan a card",
    languageLegend: "Card language",
    languages: { en: "English", ja: "Japanese", "zh-Hans": "Chinese" } as Record<
      CardLanguage,
      string
    >,
    languageCodes: { en: "EN", ja: "JA", "zh-Hans": "ZH" } as Record<CardLanguage, string>,
    hint: "Fill the frame with the card. Use good light and tilt it slightly to avoid glare on holos.",
    onlineReaderNotice:
      "Cards are read on this device first. If that gives no clear match, the photo is sent to Anthropic's Claude to read.",
    viewfinder: "Camera preview",
    starting: "Starting camera…",
    capture: "Take photo",
    upload: "Upload photo",
    uploadArea: "Upload a photo",
    uploadPrompt: "Choose a photo of your card",
    dropHint: "or drag and drop it here",
    useCamera: "Use camera",
    backToUpload: "Back to upload",
    cameraApp: "Use camera app",
    retry: "Try again",
    working: "Processing…",
    noCamera: {
      title: "No camera found",
      body: "Upload a photo of your card instead.",
    },
    cameraErrors: {
      "permission-denied": {
        title: "Camera access is blocked",
        body: "To scan with the camera, allow camera access for this site in your browser's site settings (usually behind the icon next to the address bar), then tap Try again. You can upload a photo in the meantime.",
      },
      "in-use": {
        title: "Camera is busy",
        body: "Another app or tab may be using the camera. Close it and tap Try again, or upload a photo.",
      },
      "insecure-context": {
        title: "Camera unavailable",
        body: "The camera only works over a secure (HTTPS) connection. You can still upload a photo.",
      },
      unsupported: {
        title: "Camera unavailable",
        body: "This browser can't open the camera. You can still upload a photo.",
      },
      unknown: {
        title: "Couldn't start the camera",
        body: "Tap Try again, or upload a photo instead.",
      },
    } as Record<Exclude<CameraErrorKind, "no-camera">, { title: string; body: string }>,
    uploadErrors: {
      "not-image": "That file isn't an image. Choose a photo of your card.",
      empty: "That file is empty. Choose a photo of your card.",
      unreadable: "This browser can't read that image format. Try a JPEG or PNG.",
    },
    captureFailed: "Couldn't take the photo. Try again.",
    languageNames: { en: "English", ja: "Japanese", "zh-Hans": "Simplified Chinese" } as Record<
      CardLanguage,
      string
    >,
    model: {
      loading: (language: string) =>
        `Loading the ${language} text model… The first time, this downloads a few MB.`,
      failed: (language: string) =>
        `Couldn't download the ${language} text model. English cards can still be scanned.`,
      retry: "Try again",
    },
  },
  result: {
    title: "Scan result",
    photoAlt: "Your card photo",
    reading: "Reading the card…",
    matching: "Looking up the card…",
    askingOnline: "No clear match yet, so Claude is reading the card…",
    scanAgain: "Scan again",
    searchManually: "Search manually",
    read: {
      title: "What we read",
      setCode: "Set code",
      number: "Number",
      printedTotal: "Set total",
      name: "Name",
      nothing: "Nothing readable",
      edit: "Edit",
      cancel: "Cancel",
      searchAgain: "Search again",
      lowConfidence: "unsure",
      byClaude: "Read by Claude",
      onDevice: "Read on this device",
    },
    glareHint:
      "The card number was hard to read, possibly because of glare. Tilt the card slightly and scan again for a better match.",
    bestMatch: "Best match",
    notThisCard: "Not this card?",
    candidatesTitle: "Is it one of these?",
    candidatesHint: "We're not sure which card this is. Pick the right one.",
    backToBest: "Back to best match",
    chooseDifferent: "Choose a different card",
    matchScore: (percent: number) => `${percent}% match`,
    set: "Set",
    number: "Number",
    rarity: "Rarity",
    noImage: "No image",
    addToPortfolio: "Add to portfolio",
    noMatch: {
      title: "No matching card found",
      body: "Check what we read above and tap Edit to fix it, or search for the card by hand.",
      wrongLanguage: (language: string) =>
        `Is this really a ${language} card? If not, switch the card language and scan again.`,
      missingSet: (setCode: string) =>
        `The card database knows set ${setCode} but doesn't list its cards yet.`,
    },
    errors: {
      "model-download": "Couldn't download the text model needed to read this card.",
      "unreadable-image": "This photo couldn't be read. Try scanning again.",
      unknown: "Something went wrong while reading the card.",
      service: "The online card reader isn't available right now.",
      network: "Couldn't reach the card database. Check your connection.",
      "rate-limit": "The card database is busy. Wait a moment and try again.",
      server: "The card database had a problem. Try again in a moment.",
      "not-found": "The card database didn't recognise this card.",
      retry: "Try again",
    },
  },
  search: {
    title: "Search cards",
    name: "Card name",
    namePlaceholder: "e.g. Pikachu, ピカチュウ, 皮卡丘",
    number: "Number",
    numberPlaceholder: "e.g. 25",
    set: "Set",
    allSets: "All sets",
    setCode: (code: string) => `Set code ${code}`,
    setsLoading: "Loading sets…",
    setsFailed: "Sets couldn't load",
    clear: "Clear",
    start: "Type a card name, or pick a set or number.",
    loading: "Searching…",
    updating: "Updating results…",
    resultCount: (shown: number, total: number) =>
      shown < total
        ? `Showing ${shown} of ${total} cards`
        : `${total} ${total === 1 ? "card" : "cards"}`,
    showMore: "Show more",
    noResults: {
      title: "No cards found",
      tips: [
        "Check the card language: names are searched in the language selected above.",
        "Try fewer words, or just part of the name.",
        "Remove the set or number filter.",
      ],
      zh: "Simplified Chinese card lists are still sparse in the card database, so many cards can't be found yet.",
    },
    noImage: "No image",
    noPrice: "No price",
    priceTrend: "trend",
    errors: {
      network: "Couldn't reach the card database. Check your connection.",
      "rate-limit": "The card database is busy. Wait a moment and try again.",
      server: "The card database had a problem. Try again in a moment.",
      "not-found": "The card database didn't recognise this search.",
      unknown: "Something went wrong while searching.",
      kept: "The results below are from your previous search.",
      retry: "Try again",
    },
  },
  card: {
    back: "Back",
    loading: "Loading card…",
    set: "Set",
    number: "Number",
    rarity: "Rarity",
    language: "Language",
    zoom: (name: string) => `Enlarge image of ${name}`,
    closeZoom: "Close",
    price: "Cardmarket price",
    value: "Value",
    /** The figure used as value; trend when the chosen field is missing, e.g. "trend (no 30-day average)". */
    basis: (basis: PriceField, field: PriceField) =>
      basis === "trend" && field !== "trend"
        ? `trend (no ${fieldNames[field]})`
        : fieldNames[basis],
    fields: {
      avg30: "30-day average",
      trend: "Trend",
      avg7: "7-day average",
      avg1: "1-day average",
      low: "Lowest",
      avg: "Average sell",
      market: "Market",
    } as Record<"avg30" | "trend" | "avg7" | "avg1" | "low" | "avg" | "market", string>,
    tcgplayerMid: "Mid",
    variants: {
      normal: "Normal",
      holo: "Holo",
      reverseHolo: "Reverse holo",
      firstEdition: "1st edition",
    } as Record<string, string>,
    sources: { cardmarket: "Cardmarket", tcgplayer: "TCGplayer" },
    via: (provider: string) => `via ${provider}`,
    providerNames: { tcgdex: "TCGdex", pokemontcg: "Pokémon TCG API", ptcgdata: "PTCG-card-data" },
    englishPrint: "English print price",
    updated: (date: string) => `Updated ${date}`,
    usedForValue: "used as value",
    noPrice: "No market price",
    noPriceBody:
      "The free price sources have no Cardmarket price for this card, which is common for Japanese and Chinese cards.",
    tcgplayerTitle: "TCGplayer (USD)",
    tcgplayerNote: "For information only: not used in totals.",
    converted: (eur: string) => `${eur} on Cardmarket, converted at the ECB rate`,
    conditionTitle: "By condition (estimate)",
    conditionNote: "Market prices are for Near Mint. Other conditions are an estimate.",
    holdings: "In your portfolio",
    notInPortfolio: "Not in your portfolio.",
    addToPortfolio: "Add to portfolio",
    manualValueHint: "To use your own value instead, edit an entry and set a manual value.",
    edit: "Edit",
    remove: "Remove",
    error: "Couldn't load this card.",
    notFound: "Card data unavailable: the card database doesn't know this card.",
    retry: "Try again",
  },
  portfolio: {
    title: "Portfolio",
    entries: "Portfolio entries",
    removed: (n: number) => (n === 1 ? "Removed 1 entry" : `Removed ${n} entries`),
    undo: "Undo",
    summary: {
      title: "Summary",
      totalCards: "Cards",
      uniqueCards: "Unique",
      totalValue: "Total value",
      costBasis: "Cost basis",
      gain: "Gain / loss",
      unpriced: (n: number) =>
        `${n} ${n === 1 ? "entry" : "entries"} without a price (not in the total value)`,
      withoutPurchasePrice: (n: number) =>
        `${n} ${n === 1 ? "entry" : "entries"} without a purchase price (not in gain / loss)`,
    },
    refresh: {
      button: "Refresh prices",
      running: "Refreshing prices…",
      progress: (done: number, total: number) => `${done} of ${total} cards`,
      done: "Prices updated",
      failed: (n: number) =>
        `Prices updated. ${n} ${n === 1 ? "card" : "cards"} couldn't be refreshed.`,
      allFresh: "All prices are up to date: they were fetched in the last 24 hours.",
      fresh: (n: number) =>
        `${n} ${n === 1 ? "card was" : "cards were"} already up to date (fetched in the last 24 hours).`,
      lastRefreshed: (time: string) => `Last refreshed ${time}`,
      never: "Not refreshed yet",
    },
    empty: {
      title: "Your portfolio is empty",
      body: "Add cards from a scan or a search. Your portfolio is saved to your account.",
      scan: "Scan your first card",
      import: "Import a portfolio",
    },
    toolbar: {
      search: "Search portfolio",
      searchPlaceholder: "Name, number or set",
      sort: "Sort by",
      sortKeys: {
        value: "Value",
        name: "Name",
        set: "Set",
        language: "Language",
        addedAt: "Date added",
        gain: "Gain / loss",
      },
      ascending: "Ascending",
      descending: "Descending",
      language: "Language",
      allLanguages: "All languages",
      set: "Set",
      allSets: "All sets",
      condition: "Condition",
      allConditions: "All conditions",
      graded: "Graded",
      priced: "Price",
      pricedAll: "Priced and unpriced",
      pricedOnly: "Priced",
      unpricedOnly: "Unpriced",
      clearFilters: "Clear filters",
      view: "View",
      grid: "Grid",
      list: "List",
      select: "Select",
      done: "Done",
      selected: (n: number) => `${n} selected`,
      selectAll: "Select all",
      deleteSelected: "Delete",
      shown: (shown: number, total: number) =>
        shown === total
          ? `${total} ${total === 1 ? "entry" : "entries"}`
          : `${shown} of ${total} entries`,
      noMatches: "No entries match the search or filters.",
    },
    entry: {
      value: "Value",
      cost: "Cost",
      gain: "Gain / loss",
      noValue: "—",
      manual: "manual value",
      graded: "your graded value",
      select: (name: string) => `Select ${name}`,
    },
    form: {
      addTitle: "Add to portfolio",
      editTitle: "Edit entry",
      variant: "Variant",
      condition: "Condition",
      conditions: {
        NM: "Near Mint",
        LP: "Lightly Played",
        MP: "Moderately Played",
        HP: "Heavily Played",
        DMG: "Damaged",
      },
      graded: "Graded card",
      gradingCompany: "Grading company",
      grade: "Grade",
      chooseGrade: "Choose…",
      quantity: "Quantity",
      purchasePrice: (currency: string) => `Purchase price per card (${currency})`,
      purchaseDate: "Purchase date",
      manualValue: (currency: string) => `Manual value per card (${currency})`,
      manualValueHint:
        "Used instead of the market price, and never overwritten by a price refresh.",
      gradedValue: (currency: string) => `Graded value per card (${currency})`,
      gradedValueHint:
        "Market prices are for ungraded cards. Enter what a slab like yours sells for, e.g. from recent sold listings.",
      notes: "Notes",
      attachPhoto: "Attach my scan photo",
      optional: "optional",
      add: "Add",
      save: "Save",
      cancel: "Cancel",
      added: "Added to your portfolio.",
      viewPortfolio: "View portfolio",
      errors: {
        quantityWhole: "Enter a whole number.",
        quantityMin: "Quantity must be at least 1.",
        priceNumber: "Enter an amount, e.g. 2.50.",
        priceNegative: "The amount can't be negative.",
        dateInvalid: "Enter a valid date.",
        dateFuture: "The purchase date can't be in the future.",
        gradeRequired: "Choose the grade.",
        gradedValueRequired: "Enter what this graded card is worth.",
      },
      duplicate: {
        title: "Already in your portfolio",
        body: (quantity: number, adding: number) =>
          `You already have ${quantity} of this card in this variant and condition or grade. Add ${adding} to that entry, making ${quantity + adding}?`,
        merge: "Increase quantity",
        separate: "Keep as a separate entry",
      },
      zero: {
        title: "Remove this entry?",
        body: "The quantity is 0. Remove the entry from your portfolio instead?",
        remove: "Remove entry",
        back: "Go back",
      },
    },
  },
};
